import React, { useEffect, useState } from 'react';

import {
    CreateWeightsProfilesInput,
    GetPaperPlanMetadataInput,
    GetPaperPlanMetadataOutput,
    ModifyPaperPlanInput,
    ModifyPaperPlanOutput,
    PaperPlanMetadata
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';

import { isEqual } from 'lodash';
import { useHistory, useLocation, useParams } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { DEFAULT_PLAN_WEIGHTS_MAP } from 'src/components/AddPaperPlan/AddWeights/addWeightsUtils';
import { PlanSettings } from 'src/components/AddPaperPlan/PlanSettings';
import { BreadcrumbWrapper } from 'src/components/Breadcrumb';
import { ConfirmationModal } from 'src/components/ConfirmationModal';
import { StepTracker } from 'src/components/StepTracker';
import { DEFAULT_TOASTER_TIMEOUT, PlanWeightsTab } from 'src/constants';
import { defaultPlanValues } from 'src/constants/paperPlans';
import { ADMIN_ROLE_SET } from 'src/constants/role';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { useTitle } from 'src/hooks/useTitle';
import { IBreadcrumb } from 'src/types/IBreadcrumb';
import { IEditPaperPlanParams, IPlanValues } from 'src/types/IPaperPlan';
import { IFirstPartyCorePlan, IMultiplierType } from 'src/types/IRateCard';
import { IStepTrackerStep } from 'src/types/IStepTrackerStep';
import { IToast } from 'src/types/IToaster';
import { IWeightsProfile } from 'src/types/IWeightsProfile';
import {
    getCorePlan,
    getPaperPlanAttributesInput,
    getPlanDiscountRate,
    getPlanGroup,
    getPlanYYYYMMDDUTCDate,
    getTerritory,
    getWeightsProfilesInput,
    mapPaperPlanMetadataToPlanValues
} from 'src/util/PaperPlans/PaperPlanUtils';
import { getDerivedMultiplierName } from 'src/util/RateCards';
import { getDateAndTimestamp } from 'src/util/dateTimeUtil';

import { PlanDetails } from './PlanDetails';
import { PlanReview } from './PlanReview';

interface IEditPaperPlanProps {
    corePlanList: IFirstPartyCorePlan[];
    multiplierTypeList: IMultiplierType[];
}

export const EditPaperPlan = ({ corePlanList, multiplierTypeList }: IEditPaperPlanProps) => {
    const { setToasts, userInfo, setTopAlertMsg, userRole } = React.useContext(AppContext);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
    const [planValues, setPlanValues] = useState(defaultPlanValues);
    const [initialPlanValues, setInitialPlanValues] = useState(defaultPlanValues);
    const [showRerunAlert, setShowRerunAlert] = useState<boolean>(false);
    const [planWeights, setPlanWeights] = useState(DEFAULT_PLAN_WEIGHTS_MAP);
    const [isLoading, setIsLoading] = React.useState(false);

    useTitle(`Editing PAPER plan: ${planValues.paperPlanName}`);

    const history = useHistory();

    const breadcrumbItems: IBreadcrumb[] = [
        { link: ROUTES.PAPER_PLANS.INDEX, title: 'PAPER plans' },
        { link: '', title: 'Edit plan' }
    ];

    const steps: IStepTrackerStep[] = [
        {
            stepId: ROUTES.PAPER_PLANS.STEP.DETAILS,
            stepTitle: 'Edit plan details',
            StepComponent: PlanDetails
        },
        {
            stepId: ROUTES.PAPER_PLANS.STEP.REVIEW,
            stepTitle: 'Review and save',
            StepComponent: PlanReview
        }
    ];

    if (initialPlanValues.isDraft || ADMIN_ROLE_SET.has(userRole)) {
        steps.unshift({
            stepId: ROUTES.PAPER_PLANS.STEP.SETTINGS,
            stepTitle: 'Edit plan settings',
            StepComponent: PlanSettings
        });
    }

    const { pathname } = useLocation();

    const params: IEditPaperPlanParams = useParams();
    const paperPlanId = parseInt(params.paperPlanId);

    const step = `/${pathname.split('/').pop()}`;
    const currentStepIndex = steps.findIndex(({ stepId }) => stepId === step);

    const EDIT_PAPER_PLAN_BASE_PATH = `${ROUTES.PAPER_PLANS.INDEX}${ROUTES.PAPER_PLANS.EDIT}/${paperPlanId}`;

    useEffect(() => {
        contractConfigurationClient
            .getPaperPlanMetadata(GetPaperPlanMetadataInput.clone({ paperPlanId: paperPlanId }))
            .subscribe(
                (response: GetPaperPlanMetadataOutput) => {
                    if (response.planMetadata && response.planMetadata.length > 0) {
                        const planMetadata: PaperPlanMetadata = response.planMetadata[0];
                        const planValues = mapPaperPlanMetadataToPlanValues(planMetadata);
                        const planValuesAfterProcessing = {
                            ...planValues,
                            startDate: getPlanYYYYMMDDUTCDate(planMetadata.paperPlan?.startDate || ''),
                            endDate: planMetadata.paperPlan?.endDate
                                ? getPlanYYYYMMDDUTCDate(planMetadata.paperPlan?.endDate, true)
                                : '',
                            promoMultiplier: planValues.promoMultiplier ?? '1'
                        };
                        setPlanValues(planValuesAfterProcessing as IPlanValues);
                        setInitialPlanValues(planValuesAfterProcessing as IPlanValues);
                    }
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('getPaperPlanMetadata unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            );
    }, []);

    useEffect(() => {
        if (!isEqual(defaultPlanValues, initialPlanValues)) {
            setShowRerunAlert(checkRerunAlertFieldsChange());
        }
    }, [planValues]);

    const handlePlanValuesChange = (key: string, value: string | boolean) => {
        setPlanValues((prevState) => ({
            ...prevState,
            [key]: value
        }));
    };

    const handlePlanWeightsChange = (vendor: PlanWeightsTab, key: string, value: string) => {
        setPlanWeights((previousMap) => {
            const vendorWeights = previousMap.get(vendor)!;
            const newVendorWeights = {
                ...vendorWeights,
                [key]: value
            };

            return new Map(previousMap.set(vendor, newVendorWeights));
        });
    };

    const handlePlanWeightsBusinessRulesChange = (planWeights: Map<PlanWeightsTab, IWeightsProfile>) => {
        setPlanWeights(planWeights);
    };

    const onNext = () => {
        history.push(`${EDIT_PAPER_PLAN_BASE_PATH}${steps[currentStepIndex + 1].stepId}`);
    };

    const onPrevious = () => {
        history.push(`${EDIT_PAPER_PLAN_BASE_PATH}${steps[currentStepIndex - 1].stepId}`);
    };

    const getPaperPlanInput = (isDraftPlan: boolean) => {
        const planStartDate = getDateAndTimestamp(planValues.startDate)[0];
        const planEndDate = getDateAndTimestamp(planValues.endDate)[0];
        const somePlanObj = {
            renewalCadence: planValues.renewalCadence,
            planGroup: getPlanGroup(planValues),
            promoDurationInDays: planValues.promoDurationInDays
        } as Record<string, string | boolean | number>;
        const derivedMultiplierName: string = getDerivedMultiplierName(somePlanObj);
        const corePlanDisplayName: string = getCorePlan(planValues);
        return {
            paperPlanId: Number(planValues.paperPlanId),
            paperPlanName: planValues.paperPlanName,
            displayName: planValues.displayName,
            description: planValues.description,
            territory: getTerritory(planValues.territory),
            startDate: planStartDate,
            endDate: planEndDate || undefined,
            isDraft: isDraftPlan,
            changedBy: userInfo.username,
            entityTypeId: 1,
            attributeSetId: planValues.attributeSetId,
            attributes: getPaperPlanAttributesInput(planValues),
            ...(!isDraftPlan && {
                planDiscountRate: getPlanDiscountRate(
                    derivedMultiplierName,
                    multiplierTypeList,
                    corePlanDisplayName,
                    corePlanList
                )
            })
        } as ModifyPaperPlanInput;
    };

    const onSubmit = () => {
        setIsLoading(true);
        const paperPlanInput = getPaperPlanInput(false);
        contractConfigurationClient
            .modifyPaperPlan(ModifyPaperPlanInput.clone(paperPlanInput))
            .toPromise()
            .then((response: ModifyPaperPlanOutput) => {
                const createdPlanIds = response.modifiedPaperPlanId!;
                const planWeightsInput = getWeightsProfilesInput(userInfo.username, planWeights, [createdPlanIds]);
                return contractConfigurationClient
                    .createWeightsProfiles(CreateWeightsProfilesInput.clone(planWeightsInput))
                    .toPromise();
            })
            .then(() => {
                const toast: IToast = {
                    id: 'edit-plan-toast',
                    timeout: DEFAULT_TOASTER_TIMEOUT,
                    message: 'PAPER plan updated'
                };
                setToasts([toast]);
                history.push(ROUTES.PAPER_PLANS.INDEX);
            })
            .catch((error: UnknownCoralError) => setTopAlertMsg(error.receivedException.message))
            .finally(() => {
                setIsLoading(false);
            });
    };

    const onSaveDraft = () => {
        const paperPlanInput = getPaperPlanInput(true);
        contractConfigurationClient.modifyPaperPlan(ModifyPaperPlanInput.clone(paperPlanInput)).subscribe(
            () => {
                const toast: IToast = {
                    id: 'edit-plan-toast',
                    timeout: DEFAULT_TOASTER_TIMEOUT,
                    message: '1 plan saved as draft'
                };
                setToasts([toast]);
                history.push(ROUTES.PAPER_PLANS.INDEX);
            },
            (error: UnknownCoralError) => {
                console.error('modifyPaperPlan unexpected error', error.receivedException.message);
                setTopAlertMsg(error.receivedException.message);
            }
        );
    };

    const cancelPath = `${ROUTES.PAPER_PLANS.INDEX}/${paperPlanId}`;

    const onCancel = () => {
        if (!isEqual(planValues, initialPlanValues)) {
            setIsCancelModalOpen(true);
        } else {
            history.push(cancelPath);
        }
    };

    const handleBreadcrumbRedirect = () => {
        if (!isEqual(planValues, initialPlanValues)) {
            setIsCancelModalOpen(true);
            return true;
        } else {
            return false;
        }
    };

    const checkRerunAlertFieldsChange = () => {
        const alertFields = [
            'endDate',
            'wholesalePrice',
            'endUserPrice',
            'wholesalePriceCurrency',
            'endUserPriceCurrency',
            'promoMultiplier'
        ];

        for (const field of alertFields) {
            //@ts-ignore
            if (['', undefined].includes(planValues[field]) && ['', undefined].includes(initialPlanValues[field])) {
                continue;
            }

            //@ts-ignore
            if (planValues[field] !== initialPlanValues[field]) {
                return true;
            }
        }

        return false;
    };

    return (
        <Column alignmentHorizontal="center" spacingInset="500 500 600">
            <Column width={950} maxWidth="100%" spacing="500">
                <BreadcrumbWrapper breadcrumbItems={breadcrumbItems} handleRedirect={handleBreadcrumbRedirect} />

                <Row alignmentVertical="top" wrap="down" widths={['fit', 'fill']}>
                    <Column minWidth="220px" alignmentHorizontal="start" spacing="200">
                        <StepTracker
                            steps={steps}
                            basePath={EDIT_PAPER_PLAN_BASE_PATH}
                            currentStepIndex={currentStepIndex}
                        />
                    </Column>
                    <Column spacing="300" maxWidth="550px">
                        <Switch>
                            {steps.map(({ stepId, StepComponent }) => (
                                <Route key={stepId} path={`${EDIT_PAPER_PLAN_BASE_PATH}${stepId}`}>
                                    <StepComponent
                                        planValues={planValues}
                                        setPlanValues={setPlanValues}
                                        handlePlanValuesChange={handlePlanValuesChange}
                                        handlePlanWeightsChange={handlePlanWeightsChange}
                                        handlePlanWeightsBusinessRulesChange={handlePlanWeightsBusinessRulesChange}
                                        planWeights={planWeights}
                                        steps={steps}
                                        onCancel={onCancel}
                                        onPrevious={onPrevious}
                                        onNext={onNext}
                                        onSubmit={onSubmit}
                                        isSubmitLoading={isLoading}
                                        onSaveDraft={onSaveDraft}
                                        showRerunAlert={showRerunAlert}
                                    />
                                </Route>
                            ))}
                        </Switch>
                    </Column>
                </Row>
            </Column>

            <ConfirmationModal
                isOpen={isCancelModalOpen}
                onClose={() => setIsCancelModalOpen(false)}
                title="Cancel update plan"
                body="Are you sure you want to cancel? Any changes will be lost."
                primaryButtonText="Cancel update plan"
                primaryButtonOnClick={() => {
                    history.push(cancelPath);
                }}
                secondaryButtonText="Go back"
                secondaryButtonOnClick={() => setIsCancelModalOpen(false)}
            />
        </Column>
    );
};
